import { useRef, useState } from "react";
import { EduNav } from "@/components/robotlab/EduNav";
import { FullscreenButton } from "@/components/robotlab/FullscreenButton";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Play, CheckCircle2, ChevronDown, Lock, Clock, Users } from "lucide-react";
import { courseById, courseImg } from "@/lib/advancedCourses";
import { ALL_PROJECTS, imgUrl } from "@/lib/robotLab";

const inr = (n) => `₹${n.toLocaleString("en-IN")}`;

const Module = ({ m, i }) => {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`module-${i}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#F7FAFF]" data-testid={`module-toggle-${i}`}>
        <span className="w-8 h-8 rounded-xl bg-[#0A192F] text-white grid place-items-center text-sm font-black shrink-0">{i + 1}</span>
        <span className="flex-1 font-bold text-[#0A192F]">{m.title}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <ul className="px-4 pb-4 pl-[60px] space-y-1.5 text-sm text-slate-600">{m.topics.map((t) => <li key={t} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#007A8A] shrink-0 mt-0.5" />{t}</li>)}</ul>}
    </div>
  );
};

const Project = ({ title }) => {
  const p = ALL_PROJECTS.find((x) => x.title === title);
  const inner = (
    <>
      <div className="relative aspect-[4/3] overflow-hidden bg-[#E8F0FE]">
        {p && <img src={imgUrl(p.img)} alt={title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
        {(!p || p.locked) && <div className="absolute inset-0 bg-[#0A192F]/45 grid place-items-center"><Lock className="w-5 h-5 text-white" /></div>}
      </div>
      <div className="p-3"><div className="text-sm font-bold text-[#0A192F] leading-snug">{title}</div><div className={`text-xs font-bold mt-0.5 ${p && !p.locked ? "text-[#FF2E63]" : "text-slate-400"}`}>{p && !p.locked ? "Open in Robot Lab" : "Coming soon"}</div></div>
    </>
  );
  const cls = "group block rounded-2xl overflow-hidden bg-white border border-slate-200 hover:-translate-y-0.5 transition-transform";
  return p && !p.locked ? <Link to={`/robot-lab/${p.id}`} className={cls} data-testid={`course-project-${p.id}`}>{inner}</Link> : <div className={cls} data-testid="course-project-locked">{inner}</div>;
};

export default function CourseDetail() {
  const { id } = useParams();
  const c = courseById(id);
  const [ti, setTi] = useState(0);
  const ref = useRef(null);
  if (!c) return <Navigate to="/advanced-courses" replace />;
  const tr = c.tracks[ti];
  return (
    <div ref={ref} className="bg-[#F4F7FC] min-h-screen overflow-y-auto" data-testid={`course-page-${c.id}`}>
      <section className="relative overflow-hidden bg-[#0A192F] text-white">
        <img src={courseImg(c)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] via-[#0A192F]/85 to-transparent" />
        <div className="relative max-w-6xl mx-auto px-5 lg:px-8 py-12 lg:py-16">
          <div className="flex flex-wrap items-center justify-between gap-3"><div className="flex flex-wrap items-center gap-3"><Link to="/advanced-courses" className="inline-flex items-center gap-1 text-sm text-slate-200 hover:text-white" data-testid="course-back-link"><ArrowLeft className="w-4 h-4" />All courses</Link><EduNav /></div><FullscreenButton target={ref} testid="course-fullscreen-btn" className="bg-white/10 backdrop-blur border border-white/20 hover:bg-[#FF2E63]" /></div>
          <h1 className="rl-rise mt-6 font-['Outfit'] font-black text-4xl sm:text-5xl lg:text-6xl max-w-3xl" data-testid="course-title">{c.title}</h1>
          <p className="mt-3 text-lg text-[#4FD1C5] font-semibold">{c.tag}</p>
          <p className="mt-3 max-w-2xl text-slate-300">{c.overview}</p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link to={`/advanced-courses/${c.id}/lesson`} className="inline-flex items-center gap-2 rounded-full bg-[#FF2E63] hover:bg-[#007A8A] px-7 py-3.5 font-bold transition-colors" data-testid="start-learning-btn"><Play className="w-4 h-4 fill-current" />Start Learning — free lesson</Link>
            <span className="inline-flex items-center gap-1.5 text-sm text-slate-300"><Users className="w-4 h-4" />{c.for}</span>
          </div>
        </div>
      </section>
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-12 grid lg:grid-cols-[1fr_320px] gap-8">
        <div className="space-y-10">
          <section>
            <h2 className="font-['Outfit'] font-black text-2xl text-[#0A192F]">What you'll learn</h2>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">{c.outcomes.map((o) => <div key={o} className="rounded-2xl bg-white border border-slate-200 p-4 flex gap-2 text-sm"><CheckCircle2 className="w-5 h-5 text-[#3BB273] shrink-0" />{o}</div>)}</div>
          </section>
          <section data-testid="course-syllabus">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="font-['Outfit'] font-black text-2xl text-[#0A192F]">Module-wise syllabus</h2>
              {c.tracks.length > 1 && <div className="inline-flex rounded-full bg-white border border-slate-200 p-1">{c.tracks.map((t, i) => (
                <button key={t.name} onClick={() => setTi(i)} data-testid={`track-tab-${t.name.toLowerCase()}`} className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${ti === i ? "bg-[#0A192F] text-white" : "text-[#0A192F]"}`}>{t.name}</button>))}</div>}
            </div>
            <div className="mt-4 space-y-2.5">{tr.modules.map((m, i) => <Module key={`${tr.name}-${i}`} m={m} i={i} />)}</div>
          </section>
          <section data-testid="course-projects">
            <h2 className="font-['Outfit'] font-black text-2xl text-[#0A192F]">Related projects</h2>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">{c.projects.map((p) => <Project key={p} title={p} />)}</div>
          </section>
        </div>
        <aside className="lg:sticky lg:top-24 h-fit space-y-3">
          {c.tracks.map((t, i) => (
            <div key={t.name} className={`rounded-3xl border p-5 ${i === ti ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white border-slate-200"}`} data-testid={`track-card-${t.name.toLowerCase()}`}>
              <div className={`text-xs font-bold ${i === ti ? "text-[#FFD166]" : "text-[#007A8A]"}`}>{`${t.name.toUpperCase()} TRACK`}</div>
              <div className="mt-1 font-['Outfit'] font-black text-3xl">{inr(t.fee)}<span className="text-sm font-semibold opacity-70"> + GST</span></div>
              <div className="mt-2 text-sm opacity-80 flex items-center gap-1.5"><Clock className="w-4 h-4" />{t.duration}</div>
              <div className="text-sm opacity-80">{`${t.modules.length} modules · certificate`}</div>
            </div>))}
          <Link to={`/advanced-courses/${c.id}/lesson`} className="block text-center rounded-full bg-[#FF2E63] hover:bg-[#007A8A] text-white px-6 py-3 font-bold transition-colors" data-testid="start-learning-btn-side">Start Learning</Link>
          <p className="text-xs text-slate-500">Sample fees — subject to change.</p>
        </aside>
      </div>
    </div>
  );
}
